import {
  ModalState,
  ModalStateFactory
} from 'containers/Common/ModalStateFactory';
import { Order } from 'core/order/Order';
import { ScheduleConfig, ScheduleData } from 'core/rtbCampaign/ScheduleData';
import {
  ModifyScheduleState,
  ScheduleSummaryState
} from './ScheduleManagementStates';
import { reduce } from 'lodash';
import moment from 'moment';

export enum ScheduleManagementAction {
  BatchEditSchedule = 'batchEditSchedule'
}

export class DefaultScheduleManagementStateFactory implements ModalStateFactory<ScheduleData> {
  constructor(
    protected order: Order,
    protected campaigns: {
      id: number;
      name: string;
      startDate: string;
      endDate: string;
    }[]
  ) {}

  async initStates(
    goModalStateFn: (
      state: ModalState,
      beforeGoState?: () => Promise<void>
    ) => void,
    closeModalFn: (dirty: boolean, beforeClose?: () => Promise<void>) => void,
    getContextData: () => ScheduleData | undefined
  ): Promise<ModifyScheduleState> {
    // Initialize the modify schedule state with the schedule data
    const modifyScheduleState = this.initModifyScheduleState(
      goModalStateFn,
      closeModalFn,
      getContextData
    );

    return Promise.resolve(modifyScheduleState);
  }

  private initModifyScheduleState(
    goModalStateFn: (
      state: ModalState,
      beforeGoState?: () => Promise<void>
    ) => void,
    closeModalFn: (dirty: boolean, beforeClose?: () => Promise<void>) => void,
    getContextData: () => ScheduleData | undefined
  ) {
    const scheduleConfig: ScheduleConfig = this.getScheduleConfig();

    // Initialize the modify schedule state with the provided schedule data
    const modifyScheduleState = new ModifyScheduleState(
      this.campaigns,
      scheduleConfig,
      getContextData
    );

    // Initialize the schedule summary state with the campaigns
    const scheduleSummaryState = new ScheduleSummaryState(
      this.campaigns,
      scheduleConfig,
      getContextData
    );

    // Set the next and close functions for the modify schedule state
    modifyScheduleState.setNextFn(() => goModalStateFn(scheduleSummaryState));
    modifyScheduleState.setCloseFn(closeModalFn);
    // Set the back and close functions for the schedule summary state
    scheduleSummaryState.setBackFn(() => goModalStateFn(modifyScheduleState));
    scheduleSummaryState.setCloseFn(closeModalFn);
    return modifyScheduleState;
  }

  private getScheduleConfig(): ScheduleConfig {
    return reduce(
      this.campaigns,
      (acc, campaign) => {
        acc.campaigns.push({
          id: campaign.id,
          name: campaign.name,
          startDate: campaign.startDate,
          endDate: campaign.endDate
        });
        acc.minStartDate = acc.minStartDate
          ? moment
              .min(moment(acc.minStartDate), moment(campaign.startDate))
              .format('YYYY-MM-DD HH:mm:ss')
          : campaign.startDate;
        acc.maxEndDate = acc.maxEndDate
          ? moment
              .max(moment(acc.maxEndDate), moment(campaign.endDate))
              .format('YYYY-MM-DD HH:mm:ss')
          : campaign.endDate;
        return acc;
      },
      {
        order: this.order,
        campaigns: [],
        minStartDate: '',
        maxEndDate: ''
      } as ScheduleConfig
    );
  }
}
